import { Grid, Skeleton } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { DataCard } from 'components/common/data-card';
import { ErrorBoundary } from 'components/common/error-boundary';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { IStatsBarCard } from 'interfaces/session-recap/i-stats-bar-card';
import { SessionHelper } from 'lib_ts/classes/session.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { isNil } from 'lodash';
import { useMemo } from 'react';

const VALUE_FALLBACK = '-';

/** formatHitStat treats 0 as missing, which is a valid rate */
const formatRate = (v: number | null) =>
  isNil(v) || isNaN(v) ? VALUE_FALLBACK : (v * 100).toFixed(1);

export const SessionRecapStatsBar = (props: {
  loading: boolean;
  records: ISessionRecapHit[];
}) => {
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);

  const stats = useMemo(
    () => SessionRecapHelper.getStats(props.records),
    [props.records]
  );

  const hitStats = useMemo(
    () => SessionHelper.getHitsStats(props.records.map((r) => r.hit)),
    [props.records]
  );

  const cards = useMemo(() => {
    const speedUnit = MetricHelper.getSpeedUnit(sessionMachine?.isMetric, true);
    const speedConfig = {
      roundTo: 1,
      toMetricFn: sessionMachine?.isMetric ? MetricHelper.mphToKph : undefined,
    };

    // * a session is considered to be using vision if all hits had TrajektVision as the tracking device
    const isUsingVision = props.records
      .flatMap((r) => r.hit.tracking_device)
      .every((td) => td === TrackingDevice.TrajektVision);

    const output: IStatsBarCard[] = [
      {
        title: 'common.pitches',
        value: stats.pitches,
      },
      {
        title: 'hitters.recap-avg-pitch-velo',
        value: SessionHelper.formatHitStat(stats.avgPitchMPH, speedConfig),
        unit: speedUnit,
      },
      {
        title: 'hitters.recap-swings',
        value: isUsingVision ? stats.swings : VALUE_FALLBACK,
        fallbackTooltip: isUsingVision
          ? undefined
          : 'hitters.recap-swings-no-vision-tooltip',
      },
      {
        title: 'common.avg-ev',
        value: SessionHelper.formatHitStat(hitStats.avgExitMPH, speedConfig),
        unit: speedUnit,
      },
      {
        title: 'hitters.recap-contact-rate',
        value: isUsingVision ? formatRate(stats.contactRate) : VALUE_FALLBACK,
        unit: isUsingVision ? '%' : undefined,
        fallbackTooltip: isUsingVision
          ? undefined
          : 'hitters.recap-contact-rate-no-vision-tooltip',
      },
      {
        title: 'hitters.recap-whiff-rate',
        value: isUsingVision ? formatRate(stats.whiffRate) : VALUE_FALLBACK,
        unit: isUsingVision ? '%' : undefined,
        fallbackTooltip: isUsingVision
          ? undefined
          : 'hitters.recap-whiff-rate-no-vision-tooltip',
      },
      {
        title: 'hitters.recap-barrel-rate',
        value: isUsingVision ? formatRate(stats.barrelRate) : VALUE_FALLBACK,
        unit: isUsingVision ? '%' : undefined,
        fallbackTooltip: isUsingVision
          ? undefined
          : 'hitters.recap-barrel-rate-no-vision-tooltip',
      },
    ];

    return output;
  }, [props.records, stats, hitStats, sessionMachine?.isMetric]);

  return (
    <ErrorBoundary identifier="SessionRecapStatsBar">
      <Grid
        columns={{ initial: '2', xs: '4', md: '7' }}
        gap={RADIX.FLEX.GAP.XS}
      >
        {cards.map((c) => (
          <Skeleton key={c.title} loading={props.loading}>
            <DataCard
              title={c.title}
              value={c.value}
              unit={c.unit}
              fallbackTooltip={c.fallbackTooltip}
              titleSize="1"
              valueSize="5"
              unitSize="3"
            />
          </Skeleton>
        ))}
      </Grid>
    </ErrorBoundary>
  );
};
